import axios from '@fs/zion-axios'

const countriesUrl = '/service/user/profile/countries'

/**
 * wraps an axios call within a try/catch block
 * Many calls use this, but we use react-query on other calls, and it depends on catching errors
 * Hence many calls correctly do not use this.
 * */
async function wrapError(req) {
  try {
    return await req
  } catch (err) {
    return err.response
  }
}
export function getUsersCenters() {
  return wrapError(axios.get(`/service/fs/locator/admin/facility`))
}

export function createNewFacility(facility) {
  return wrapError(axios.post(`/service/fs/locator/admin/facility`, facility))
}

export function deleteFacility(facility) {
  return wrapError(axios.delete(`/service/fs/locator/admin/facility/${facility.facilityId}`, facility))
}

export function searchCenters(searchString, searchType) {
  return wrapError(
    axios.get(`/service/fs/locator/admin/search?searchType=${searchType}&searchVariable=${searchString}`)
  )
}

export function getSelectedCenter(unitNumber) {
  return wrapError(axios.get(`/service/fs/locator/admin/facility/unit-number=${unitNumber}`))
}

export async function getCountries() {
  return axios.get(countriesUrl).then((res) => {
    return res?.data?.places || []
  })
}

export function getCenters(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/facility/${facilityId}`))
}

export function updateCenterData(updates) {
  return wrapError(axios.put(`/service/fs/locator/admin/facility`, updates))
}

export function getCenterAddress(addressId) {
  return wrapError(axios.get(`/service/fs/locator/admin/address/${addressId}`))
}

export function getCenterHours(facilityId) {
  return axios.get(`/service/fs/locator/admin/hours/facility-id=${facilityId}`)
}

export function updateCenterHours(updates) {
  return wrapError(axios.post(`/service/fs/locator/admin/hours`, updates))
}
export function getCenterEvents(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/calendar/facility-id=${facilityId}`))
}

export function updateCenterRepresentatives(updates) {
  return wrapError(axios.post(`/service/fs/locator/admin/affiliateContacts`, updates))
}

export function getCenterRepresentatives(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/affiliateContacts/facility-id=${facilityId}`))
}

export function createCenterEvent(updates) {
  return wrapError(axios.post(`/service/fs/locator/admin/calendar`, updates))
}

export function updateCenterEvent(updates) {
  return wrapError(axios.put(`/service/fs/locator/admin/calendar`, updates))
}

export function deleteCenterEvent(calendarId) {
  return wrapError(axios.delete(`/service/fs/locator/admin/calendar/${calendarId}`))
}

export function getEditingPermissions() {
  return wrapError(axios.get(`/service/help/local-info/centers/editor`))
}

export function getCenterDirectorPermissions() {
  return wrapError(axios.get(`/service/help/local-info/centers/leader`))
}

export function getCenterOutageMessage() {
  return wrapError(axios.get(`/service/fs/locator/admin/systemMessage`))
}

export function downloadPrinterCSV() {
  return wrapError(
    axios.get(`/service/fs/locator/admin/printers`, {
      headers: {
        Accept: 'text/csv',
      },
    })
  )
}

export function downloadComputerCSV() {
  return wrapError(
    axios.get(`/service/fs/locator/admin/computers`, {
      headers: {
        Accept: 'text/csv',
      },
    })
  )
}

export function downloadAffiliateDetailsCSV() {
  return wrapError(
    axios.get(`/service/fs/locator/admin/affiliates`, {
      headers: {
        Accept: 'text/vnd.familysearch.fs-locator-admin.details+csv;charset=UTF-8',
      },
    })
  )
}

export function downloadAffiliateOnboardingCSV() {
  return wrapError(
    axios.get(`/service/fs/locator/admin/affiliates`, {
      headers: {
        Accept: 'text/vnd.familysearch.fs-locator-admin.onboarding+csv;charset=UTF-8',
      },
    })
  )
}

export function getUploadList(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/uploads?facilityId=${facilityId}`))
}

export function deleteUpload(uploadId) {
  return wrapError(axios.delete(`/service/fs/locator/admin/uploads/${uploadId}`))
}

export function addUpload(upload) {
  return wrapError(axios.post(`/service/fs/locator/admin/uploads`, upload))
}

export function updateUpload(upload) {
  return wrapError(axios.put(`/service/fs/locator/admin/uploads/${upload.id}`, upload))
}

export function downloadAttachment(uploadId) {
  return axios.get(`/service/fs/locator/admin/uploads/${uploadId}/file`, {
    headers: {
      Accept: 'application/octet-stream',
    },
    responseType: 'blob',
  })
}

export function getUpload(uploadId) {
  return axios
    .get(`/service/fs/locator/admin/uploads/${uploadId}/file`, {
      responseType: 'blob',
    })
    .then((response) => {
      if (response && response.data instanceof Blob) {
        return response.data
      }
      throw new Error('Response is not a Blob')
    })
    .catch((err) => {
      console.error('Error fetching upload:', err)
      throw err
    })
}

export function getPrinters(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/printer/facility-id=${facilityId}`))
}

export function getComputers(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/computer/facility-id=${facilityId}`))
}

export function editPrinter(updates) {
  return axios.put(`/service/fs/locator/admin/printer`, updates)
}

export function addPrinter(updates) {
  return axios.post(`/service/fs/locator/admin/printer`, updates)
}

export function getPrinterModels() {
  return wrapError(axios.get(`/service/fs/locator/admin/printerModel/all`))
}

export function addPrinterModel(updates) {
  return axios.post('/service/fs/locator/admin/printerModel', updates)
}

export function deletePrinterModel(modelId) {
  return axios.delete(`/service/fs/locator/admin/printerModel/${modelId}`)
}

export function deletePrinter(printerId) {
  return axios.delete(`/service/fs/locator/admin/printer/${printerId}`)
}

export function getAreaInformation(facilityId) {
  return axios.get(`/service/fs/locator/admin/fmGroup/facility-id=${facilityId}`)
}

export function getDirectorInformation(facilityId) {
  return axios.get(`/service/fs/locator/admin/director/facility-id=${facilityId}`)
}

export function getFacilityDailyReport(facilityId) {
  return axios.get(`/service/fs/locator/admin/computer-usages/${facilityId}/centersDaily`)
}

export function getComputerUsageData(facilityId) {
  return axios.get(`/service/fs/locator/admin/facilities/${facilityId}/computer-usage`)
}

/**
 * The /lookups/... endpoints return one flat, de-duplicated array of strings covering every
 * parent value sent in the request, so a single batched call loses which child belongs to
 * which parent. The center counts UI needs that parent/child grouping (to title the dialog
 * sections, to build the filterGroups sent to /facilities/counts, and to cascade chip
 * dismissals), so we request each parent separately and rebuild the
 * `{ [parentValue]: string[] }` map the callers expect.
 * */
// Browsers allow about six concurrent connections per host, so beyond this the requests
// queue in the browser rather than going out any faster. Capping keeps a broad selection
// from filling that queue with lookups and starving the counts request behind them.
const MAX_CONCURRENT_LOOKUPS = 6

// Promise.all over every parent at once is what we want semantically, but not what we want
// on the wire: the query key holds the whole parent array, so selecting the Nth parent
// refires all N requests, and a session of twenty selections issues a couple of hundred.
// Workers pull from a shared cursor, so the slowest parent never blocks the others and
// results still land in the caller's order.
async function mapWithLimit(items, limit, fn) {
  const results = new Array(items.length)
  let next = 0

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length) {
        const index = next
        next += 1
        // Sequential by design, which is the whole point of a worker: it takes the next
        // parent only once its own request has settled. The parallelism is the six
        // workers running this loop at the same time, not the loop itself.
        // eslint-disable-next-line no-await-in-loop
        results[index] = await fn(items[index])
      }
    })
  )

  return results
}

async function getLookupsGroupedByParent(url, paramName, parentValues) {
  const responses = await mapWithLimit(parentValues, MAX_CONCURRENT_LOOKUPS, (parentValue) =>
    axios.get(url, { params: { [paramName]: parentValue } })
  )

  const data = {}
  parentValues.forEach((parentValue, index) => {
    // These endpoints answer 204 No Content when a parent has no children, which is routine
    // per-parent (e.g. a country with no region data). A 204 has no body, and axios's default
    // transformResponse skips JSON parsing on an empty string, so response.data is ''. Guard on
    // the type rather than nullishness -- `'' ?? []` is '', which would put a string where
    // callers expect an array and blow up on .forEach/.filter.
    const children = responses[index]?.data
    data[parentValue] = Array.isArray(children) ? children : []
  })

  return { data }
}

/**
 * Data for center counts page and custom facility reports page.
 *
 * These four lookups are gated together on the `center-locations_lookups-endpoints` flag: a
 * half-migrated state (areas from /lookups but countries from the legacy route) would produce
 * mismatched data.
 *
 * getTempleDistricts is deliberately outside that gate and takes no flag. /lookups/templeDistricts
 * is new in this API with no /facilities/report predecessor, so there is nothing to fall back to
 * and nothing to keep consistent with - it already called the new route before this migration. Its
 * queryKey in FacilityReportsProvider omits the flag for the same reason. `useLookupsEndpoints` defaults to false so the legacy path is what runs when
 * a caller omits it or the flag is unresolved. api.js is a plain module and cannot read the
 * flag itself -- the hook layer passes it in.
 * */
export function getCountriesByArea(areas, useLookupsEndpoints = false) {
  if (!areas || areas.length === 0) {
    return axios.get(
      useLookupsEndpoints
        ? `/service/fs/locator/admin/lookups/countries`
        : `/service/fs/locator/admin/facilities/report/countries`
    )
  }
  if (!useLookupsEndpoints) {
    return axios.get('/service/fs/locator/admin/facilities/report/countries-by-areas', {
      params: { fhcAreas: areas.join(',') },
    })
  }
  return getLookupsGroupedByParent('/service/fs/locator/admin/lookups/countries', 'areas', areas)
}

export function getRegionByCountry(countries, useLookupsEndpoints = false) {
  if (!countries || countries.length === 0) {
    return Promise.resolve({ data: [] })
  }
  if (!useLookupsEndpoints) {
    return wrapError(
      axios.get('/service/fs/locator/admin/facilities/report/regions-by-countries', {
        params: { countryNames: countries.join(',') },
      })
    )
  }
  return wrapError(
    getLookupsGroupedByParent('/service/fs/locator/admin/lookups/regionStateProvinces', 'countries', countries)
  )
}

export function getCityByRegion(regionStateProvince, useLookupsEndpoints = false) {
  if (!regionStateProvince || regionStateProvince.length === 0) {
    return Promise.resolve({ data: [] })
  }
  if (!useLookupsEndpoints) {
    return axios.get('/service/fs/locator/admin/facilities/report/cities-by-regions', {
      params: { regionStateProvinces: regionStateProvince.join(',') },
    })
  }
  return getLookupsGroupedByParent(
    '/service/fs/locator/admin/lookups/cities',
    'regionStateProvinces',
    regionStateProvince
  )
}

export function getFhcAreas(useLookupsEndpoints = false) {
  if (!useLookupsEndpoints) {
    return wrapError(axios.get(`/service/fs/locator/admin/facilities/report/fhc-areas`))
  }
  return wrapError(axios.get(`/service/fs/locator/admin/lookups/areas`))
}

// center counts page
export function getCenterCounts(filterGroups, facilityType = 'FamilySearch center') {
  return axios.post(`/service/fs/locator/admin/facilities/counts`, {
    filterGroups, // array of { fhcArea, country, regionStateProvince, city }
    facilityType,
  })
}

export function getTempleDistricts(filterCountry) {
  if (filterCountry && filterCountry.length > 0) {
    return wrapError(
      axios.get(`/service/fs/locator/admin/lookups/templeDistricts`, {
        params: { countries: filterCountry.join(',') },
      })
    )
  }

  return wrapError(axios.get(`/service/fs/locator/admin/lookups/templeDistricts`))
}

// custom center reports
export function getFacilityReport(report, format = 'application/json') {
  const orderBy = report.orderByFields.map((item) => `${item.field} ${item.order}`).join(', ')

  const params = {
    fields: report.fields?.join(',') || undefined,
    orderBy,
    countryName: report.country || undefined,
    facilityType: report.fhcType || 'FamilySearch center',
    fhcArea: report.fhcArea || undefined,
    regionStateProvince: report.state || undefined,
    templeDistrict: report.templeDistrict || undefined,
    languageDisplay: report.language || undefined,
    additionalParams: report.additionalParams || undefined,
  }

  return wrapError(
    axios.get(`/service/fs/locator/admin/facilities/reports`, {
      params,
      headers: {
        Accept: format,
      },
      responseType: format === 'application/json' ? 'json' : 'blob',
    })
  )
}

export function getReportPresets() {
  return axios.get('/service/fs/locator/admin/report-presets')
}

export function createReportPreset(data) {
  // Transform the form data to match the API expected format
  const presetData = {
    name: data.reportAndPresetName,
    reportType: data.fhcType === 'FamilySearch center' ? 'FAMILYSEARCH_CENTER' : 'FAMILYSEARCH_AFFILIATE_LOCATION',
    fieldsJson: JSON.stringify(data.fields || []),
    filtersJson: JSON.stringify({
      fhcArea: data.fhcArea || undefined,
      countryName: data.country || undefined,
      regionStateProvince: data.state || undefined,
      templeDistrict: data.templeDistrict || undefined,
      facilityType: data.fhcType || undefined,
    }),
    sortOrderJson: JSON.stringify(data.orderByFields || []),
  }

  return wrapError(axios.post('/service/fs/locator/admin/report-presets', presetData))
}

export function updateReportPreset({ presetId, data }) {
  // Transform the form data to match the API expected format
  const presetData = {
    name: data.reportAndPresetName,
    reportType: data.fhcType === 'FamilySearch center' ? 'FAMILYSEARCH_CENTER' : 'FAMILYSEARCH_AFFILIATE_LOCATION',
    fieldsJson: JSON.stringify(data.fields || []),
    filtersJson: JSON.stringify({
      fhcArea: data.fhcArea || undefined,
      countryName: data.country || undefined,
      regionStateProvince: data.state || undefined,
      templeDistrict: data.templeDistrict || undefined,
      facilityType: data.fhcType || undefined,
    }),
    sortOrderJson: JSON.stringify(data.orderByFields || []),
  }

  return wrapError(axios.put(`/service/fs/locator/admin/report-presets/${presetId}`, presetData))
}

export function deleteReportPreset(presetId) {
  return wrapError(axios.delete(`/service/fs/locator/admin/report-presets/${presetId}`))
}

export function getAffiliateReportPresets() {
  return axios.get('/service/fs/locator/admin/report-presets', {
    params: { reportType: 'FAMILYSEARCH_AFFILIATE_LOCATION' },
  })
}

export function getAffiliateReportPreset(presetId) {
  return axios.get(`/service/fs/locator/admin/report-presets/${presetId}`)
}

export function createAffiliateReportPreset(presetData) {
  return wrapError(axios.post('/service/fs/locator/admin/report-presets', presetData))
}

export function updateAffiliateReportPreset({ presetId, presetData }) {
  return wrapError(axios.put(`/service/fs/locator/admin/report-presets/${presetId}`, presetData))
}
